import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { getPortalInventory } from "@/lib/portal.functions";
import { runServerFn } from "@/lib/server-fn";
import { PortalPageHeader } from "@/components/portal-shell";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { StatusPill } from "@/components/ui/status-pill";
import { Search, Download, AlertTriangle } from "lucide-react";

export const Route = createFileRoute("/_authenticated/portal/inventory")({ component: PortalInventory });

function PortalInventory() {
  const [q, setQ] = useState("");
  const [onlyLow, setOnlyLow] = useState(false);

  const fetchInventory = useServerFn(getPortalInventory);
  const { data: rowsData } = useQuery({
    queryKey: ["portal-inventory"],
    queryFn: () => runServerFn(() => fetchInventory(), "Loading inventory"),
  });
  const allRows = useMemo(() => rowsData ?? [], [rowsData]);

  const rows = useMemo(() => {
    const ql = q.trim().toLowerCase();
    return allRows.filter((r) => {
      if (onlyLow) {
        const rp = r.reorder_point ?? 0;
        if (!(rp > 0 && r.qty <= rp)) return false;
      }
      if (!ql) return true;
      return r.sku_code.toLowerCase().includes(ql) || (r.name ?? "").toLowerCase().includes(ql);
    });
  }, [allRows, q, onlyLow]);

  const exportCsv = () => {
    const header = "sku_code,name,on_hand,reorder_point";
    const body = rows.map((r) =>
      [r.sku_code, (r.name ?? "").replaceAll('"', '""'), r.qty, r.reorder_point ?? 0]
        .map((v) => `"${v}"`).join(","),
    ).join("\n");
    const blob = new Blob([header + "\n" + body], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `inventory-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click(); URL.revokeObjectURL(url);
  };

  return (
    <>
      <PortalPageHeader title="Inventory" description="On-hand units across all facilities." />
      <div className="p-4 sm:p-6 space-y-4">
        <div className="flex flex-col sm:flex-row gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input className="pl-9" placeholder="Search SKU or name…" value={q} onChange={(e) => setQ(e.target.value)} />
          </div>
          <Button variant={onlyLow ? "default" : "outline"} onClick={() => setOnlyLow((v) => !v)}>
            <AlertTriangle className="h-4 w-4 mr-2" />Low stock only
          </Button>
          <Button variant="outline" onClick={exportCsv} disabled={rows.length === 0}>
            <Download className="h-4 w-4 mr-2" />Export CSV
          </Button>
        </div>
        <div className="border rounded-lg bg-card overflow-x-auto">
          <Table>
            <TableHeader><TableRow>
              <TableHead>SKU</TableHead><TableHead>Name</TableHead>
              <TableHead className="text-right">Reorder pt</TableHead>
              <TableHead className="text-right">On hand</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {rows.length > 0 ? rows.map((r) => {
                const rp = r.reorder_point ?? 0;
                const low = rp > 0 && r.qty <= rp;
                return (
                  <TableRow key={r.id}>
                    <TableCell className="font-mono text-sm">{r.sku_code}</TableCell>
                    <TableCell>{r.name}</TableCell>
                    <TableCell className="text-right tabular-nums text-muted-foreground">{rp || "—"}</TableCell>
                    <TableCell className="text-right tabular-nums">
                      {low ? (
                        <StatusPill tone={r.qty <= 0 ? "danger" : "warning"}>{r.qty}</StatusPill>
                      ) : r.qty}
                    </TableCell>
                  </TableRow>
                );
              }) : (
                <TableRow><TableCell colSpan={4} className="text-center py-8 text-muted-foreground">No inventory matches.</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </div>
    </>
  );
}